import { useState, useCallback, useEffect } from 'react';

/**
 * IDs de pages pour les annotations Miro
 */
export type MiroPageId =
  | 'dashboard'
  | 'projects'
  | 'team'
  | 'rentabilite'
  | 'financial'
  | 'tresorerie'
  | 'reports'
  | 'settings';

const MIRO_KEY_PREFIX = 'b8ild_miro_annotations_';
const CHECKLIST_KEY = 'b8ild_onboarding_checklist_v2';

/**
 * Hook gérant la visibilité des annotations Miro pour une page donnée.
 * - S'affiche UNE SEULE FOIS (persisté en localStorage)
 * - Dashboard uniquement : attend que la checklist d'onboarding soit terminée ou masquée
 */
export function useMiroAnnotations(pageId: MiroPageId) {
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // 1. Ne pas afficher si déjà vu
    const key = `${MIRO_KEY_PREFIX}${pageId}_seen`;
    if (localStorage.getItem(key) === 'true') return;

    // 2. Condition spéciale Dashboard : attendre que la checklist soit terminée ou masquée
    if (pageId === 'dashboard') {
      const raw = localStorage.getItem(CHECKLIST_KEY);
      if (!raw) return; // Checklist jamais initialisée → ne pas afficher
      try {
        const parsed = JSON.parse(raw);
        // showAsGuide = true quand l'utilisateur a masqué ou terminé la checklist
        const isChecklistDone = parsed.userDismissed === true || parsed.showAsGuide === true;
        if (!isChecklistDone) return;
      } catch {
        return;
      }
    }

    // 2b. Condition spéciale Team : attendre que le TipsModal équipe soit vu
    // Évite la superposition entre le TipsModal (z-50 + backdrop) et les annotations (z-61)
    if (pageId === 'team') {
      if (localStorage.getItem('b8ild_tips_equipe_seen') !== 'true') return;
    }

    // 2c. Condition spéciale Financial : attendre que le TipsModal retard soit vu
    if (pageId === 'financial') {
      if (localStorage.getItem('b8ild_tips_retard_seen') !== 'true') return;
    }

    // 3. Afficher après un délai pour laisser la page finir de se rendre
    const timer = setTimeout(() => {
      setIsReady(true);
      setIsVisible(true);
    }, 400);

    return () => clearTimeout(timer);
  }, [pageId]);

  /**
   * Ferme les annotations et les marque comme vues (ne réapparaîtront plus jamais).
   * Ne bloque pas la propagation des clics.
   */
  const dismiss = useCallback(() => {
    const key = `${MIRO_KEY_PREFIX}${pageId}_seen`;
    localStorage.setItem(key, 'true');
    setIsVisible(false);
  }, [pageId]);

  /**
   * Réinitialise pour dev/test — ne pas utiliser en prod
   */
  const reset = useCallback(() => {
    const key = `${MIRO_KEY_PREFIX}${pageId}_seen`;
    localStorage.removeItem(key);
    setIsVisible(false);
    setIsReady(false);
  }, [pageId]);

  return { isVisible, isReady, dismiss, reset };
}
